import { createSelector } from 'reselect';
import * as R from 'ramda';
import * as AlertBarState from '../../components/alert-bar/AlertBar.state';
import * as Nested from '../../utils/Nested';
import {
  CASH_DROP_SUCCESS,
  SUBMIT_CASH_DROP_FORM,
} from '../cash-drop/CashDrop.state';
import {
  CREATE_CLOSEOUT_SUCCESS,
  SUBMIT_CLOSEOUT_FORM,
} from '../closeout/Closeout.state';
import { formatPrice } from '../../utils/helpers';
import {
  CASH_ACTION_TYPES,
  CASH_ACTION_TYPES_DISPLAY,
} from '../../utils/constants';

// Default State
const initialState = {
  error: false,
  data: {},
  meta: {},
  reports: {},
  isLoading: false,
};

// Action Creators and Action Types
export const GET_CASH_ACTION_DETAILS_REQUEST =
  'GET_CASH_ACTION_DETAILS_REQUEST';
export const getCashActionDetailsRequest = (data) => ({
  type: GET_CASH_ACTION_DETAILS_REQUEST,
  payload: data,
});

export const GET_CASH_ACTION_DETAILS_SUCCESS =
  'GET_CASH_ACTION_DETAILS_SUCCESS';
export const getCashActionDetailsSuccess = (data) => ({
  type: GET_CASH_ACTION_DETAILS_SUCCESS,
  payload: data,
});

export const GET_CASH_ACTION_DETAILS_ERROR = 'GET_CASH_ACTION_DETAILS_ERROR';
export const getCashActionDetailsError = (data) => ({
  type: GET_CASH_ACTION_DETAILS_ERROR,
  payload: data,
});

export const GET_CLOSEOUT_DETAILS_REQUEST = 'GET_CLOSEOUT_DETAILS_REQUEST';
export const getCloseoutDetailsRequest = (data) => ({
  type: GET_CLOSEOUT_DETAILS_REQUEST,
  payload: data,
});

export const GET_CLOSEOUT_DETAILS_SUCCESS = 'GET_CLOSEOUT_DETAILS_SUCCESS';
export const getCloseoutDetailsSuccess = (data) => ({
  type: GET_CLOSEOUT_DETAILS_SUCCESS,
  payload: data,
});

export const GET_CLOSEOUT_DETAILS_ERROR = 'GET_CLOSEOUT_DETAILS_ERROR';
export const getCloseoutDetailsError = (data) => ({
  type: GET_CLOSEOUT_DETAILS_ERROR,
  payload: data,
});

export const CLEAR_REPORTS = 'CLEAR_REPORTS';
export const clearReports = () => ({
  type: CLEAR_REPORTS,
});
// Alert Bar Nested Actions
export const ALERT_BAR_ACTION = 'cashActionDetails/ALERT_BAR_ACTION';
export const cashActionDetailsAlertBarAction =
  Nested.nestedAction(ALERT_BAR_ACTION);

// Modal Nested Actions
export const LOADING_MODAL_ACTION = 'cashActionDetails/LOADING_MODAL_ACTION';
export const cashActionDetailsLoadingModalAction =
  Nested.nestedAction(LOADING_MODAL_ACTION);

export const SUBMIT_PRINT_RECEIPT = 'cashActionDetails/SUBMIT_PRINT_RECEIPT';
export const submitPrintReceipt = (payload) => ({
  type: SUBMIT_PRINT_RECEIPT,
  payload,
});

export const POST_RECEIPT_SUCCESS = 'cashActionDetails/POST_RECEIPT_SUCCESS';
export const postReceiptSuccess = (payload) => ({
  type: POST_RECEIPT_SUCCESS,
  payload,
});
export const REPRINT_CLOSEOUT_REPORTS_REQUEST =
  'cashActionDetails/REPRINT_CLOSEOUT_REPORTS_REQUEST';
export const reprintCloseoutReports = (payload) => ({
  type: REPRINT_CLOSEOUT_REPORTS_REQUEST,
  payload,
});
export const REPRINT_CLOSEOUT_REPORTS_SUCCESS =
  'cashActionDetails/REPRINT_CLOSEOUT_REPORTS_SUCCESS';
export const reprintCloseoutReportsSuccess = (payload) => ({
  type: REPRINT_CLOSEOUT_REPORTS_SUCCESS,
  payload,
});

// Raw Reducer
export const _reducer = (state = initialState, { type, payload } = action) => {
  switch (type) {
    // Do not separate this fall through syntax
    case SUBMIT_CLOSEOUT_FORM:
    case SUBMIT_CASH_DROP_FORM:
      // case REPRINT_CLOSEOUT_REPORTS_REQUEST:
      return {
        ...state,
        fetching: true,
      };
    case CASH_DROP_SUCCESS:
      return {
        ...state,
        fetching: false,
        data: {
          type: 'cashDrop',
          ...payload,
        },
      };
    case GET_CASH_ACTION_DETAILS_REQUEST:
      return {
        ...state,
        fetching: true,
      };
    case GET_CASH_ACTION_DETAILS_SUCCESS:
      return {
        ...state,
        data: {
          type: 'cashDrop',
          ...payload,
        },
        fetching: false,
        error: false,
      };
    case GET_CASH_ACTION_DETAILS_ERROR:
      return {
        ...state,
        error: true,
        fetching: false,
        errorText: payload,
      };
    case CREATE_CLOSEOUT_SUCCESS:
      return {
        ...state,
        fetching: false,
        data: {
          type: 'closeout',
          ...payload,
        },
      };
    case GET_CLOSEOUT_DETAILS_REQUEST:
      return {
        ...state,
        fetching: true,
      };
    case GET_CLOSEOUT_DETAILS_SUCCESS:
      return {
        ...state,
        data: {
          type: 'closeout',
          ...payload,
        },
        fetching: false,
        error: false,
      };
    case REPRINT_CLOSEOUT_REPORTS_SUCCESS:
      return {
        ...state,
        reports: payload.reports,
      };
    case CLEAR_REPORTS:
      return {
        ...state,
        reports: initialState.reports,
      };
    default:
      return state;
  }
};

// Selectors
const selectCashActionData = (state) => ({
  type: state.cashActionDetails.data.type,
  ...state.cashActionDetails.data,
});
const selectFlattenCashActionData = createSelector(
  selectCashActionData,
  (data) => ({
    id: data.id || data.terminal_session?.uuid,
    batchNumber: data.uuid || data.terminal_session?.uuid,
    total:
      data.type === CASH_ACTION_TYPES.CLOSEOUT
        ? data.closeout?.total
        : data.total,
    date:
      data.type === CASH_ACTION_TYPES.CLOSEOUT
        ? data.terminal_session?.closeout_at
        : data.created_at,
    closeoutTotals: [
      {
        label: 'Cash',
        total: formatPrice(data.closeout?.cash_total_amount),
      },
      {
        label: 'Card',
        total: formatPrice(data.closeout?.credit_card_total_amount),
      },
      {
        label: 'Check',
        total: formatPrice(data.closeout?.check_total_amount),
      },
    ],
    denominations: data.closeout?.denominations?.map((denomination) => ({
      label: formatPrice(denomination.label),
      count: denomination.count,
      total: formatPrice(denomination.total),
    })),
    ...data,
  }),
);
const selectCashActionTypeDisplay = createSelector(
  selectCashActionData,
  (data) => CASH_ACTION_TYPES_DISPLAY[data.type],
);

// Raw Redux Maps
export const _mapStateToProps = (state) => ({
  ...state.cashActionDetails,
  data: selectFlattenCashActionData(state),
  typeDisplay: selectCashActionTypeDisplay(state),
  reports: R.pathOr({}, ['cashActionDetails', 'reports'], state),
});
export const _mapDispatchToProps = (dispatch) => ({
  getCloseoutDetails: (id) => dispatch(getCloseoutDetailsRequest(id)),
  getCashActionDetails: (id) => dispatch(getCashActionDetailsRequest(id)),
  submitPrintReceipt: (payload) => dispatch(submitPrintReceipt(payload)),
  submitReprintReport: (payload) => dispatch(reprintCloseoutReports(payload)),
});

// Nested Redux Maps
const {
  reducer: nestedReducer,
  mapStateToProps,
  mapDispatchToProps,
  actions,
} = Nested.nestStates(
  {
    reducer: _reducer,
    mapStateToProps: _mapStateToProps,
    mapDispatchToProps: _mapDispatchToProps,
  },
  {
    alertBars: {
      cashActionDetailsAlertBars: {
        ...AlertBarState,
        actionType: ALERT_BAR_ACTION,
      },
    },
  },
  'cashActionDetails',
);

// Nested Reducer
const reducer = (state, action) => nestedReducer(state, action);

export { reducer, mapStateToProps, mapDispatchToProps, actions };
